<template>
  <transition name="dm-notice">
    <div v-if="visible" class="dm-notice" role="status">
      <div class="dm-notice__icon" aria-hidden="true">
        <i class="fas fa-mobile-alt"></i>
      </div>
      <div class="dm-notice__body">
        <strong>{{ $t("mobileAppNotice.title") }}</strong>
        <p>{{ $t("mobileAppNotice.text", { name }) }}</p>
      </div>
      <button
        type="button"
        class="dm-notice__close"
        :aria-label="$t('Close')"
        @click="dismiss"
      >
        <i class="fas fa-times" aria-hidden="true"></i>
      </button>
    </div>
  </transition>
</template>

<script>
const STORAGE_KEY = "desktopMobileAppNotice";
/** Cerrado tantas veces, no se muestra más. */
const MAX_DISMISSALS = 3;

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

function readState() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    return { count: Number(raw.count) || 0, day: String(raw.day || "") };
  } catch (e) {
    return { count: 0, day: "" };
  }
}

/**
 * Escritorio: cuenta que existe la app para el teléfono. Se muestra hasta que el usuario la
 * cierra; vuelve recién al día siguiente, y después del tercer cierre no aparece más.
 * Guardado en este navegador (localStorage).
 */
export default {
  name: "DesktopMobileAppNotice",
  props: {
    name: { type: String, default: "" },
  },
  data() {
    return { visible: false, timer: null };
  },
  mounted() {
    const state = readState();
    if (state.count >= MAX_DISMISSALS || state.day === today()) return;
    // Un momento después de entrar, para no taparle la pantalla mientras carga.
    this.timer = setTimeout(() => {
      this.visible = true;
    }, 2500);
  },
  beforeDestroy() {
    clearTimeout(this.timer);
  },
  methods: {
    dismiss() {
      this.visible = false;
      const state = readState();
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ count: state.count + 1, day: today() })
        );
      } catch (e) {
        // sin almacenamiento: se cierra solo por esta visita
      }
    },
  },
};
</script>

<style scoped>
.dm-notice {
  position: fixed;
  right: 1.25rem;
  bottom: 1.25rem;
  z-index: 1040;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  width: 360px;
  max-width: calc(100vw - 2.5rem);
  padding: 0.9rem 0.75rem 0.9rem 0.9rem;
  border: 1px solid var(--bs-border-color);
  border-left: 4px solid var(--bs-primary);
  border-radius: 0.6rem;
  background: var(--bs-body-bg);
  color: var(--bs-body-color);
  box-shadow: var(--bs-box-shadow-lg);
}
.dm-notice__icon {
  flex-shrink: 0;
  width: 2.25rem;
  height: 2.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(var(--bs-primary-rgb), 0.12);
  color: var(--bs-primary);
  font-size: 1.1rem;
}
.dm-notice__body {
  flex: 1;
  min-width: 0;
  font-size: 0.88rem;
  line-height: 1.4;
}
.dm-notice__body p {
  margin: 0.25rem 0 0;
  color: var(--bs-secondary-color);
}
.dm-notice__close {
  flex-shrink: 0;
  width: 1.75rem;
  height: 1.75rem;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--bs-secondary-color);
}
.dm-notice__close:hover {
  background: var(--bs-secondary-bg);
}
.dm-notice-enter-active,
.dm-notice-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.dm-notice-enter,
.dm-notice-leave-to {
  opacity: 0;
  transform: translateY(0.75rem);
}

html#darkdesign .dm-notice {
  background: var(--dark-bg-card);
  border-color: var(--dark-border-color);
  border-left-color: var(--bs-primary);
  color: var(--dark-text-primary);
}
html#darkdesign .dm-notice__body p,
html#darkdesign .dm-notice__close {
  color: var(--dark-text-secondary);
}
</style>
